import * as React from 'react';
import { useTable, Column, usePagination, Row, HeaderGroup, useRowSelect } from "react-table";
import styles from './table.module.scss';
import {ActionIcon, Flex, Select, Checkbox, CheckboxProps} from "@mantine/core";
import type { TableProps } from "./types";
import {
    IconChevronLeft,
    IconChevronsLeft,
    IconChevronRight,
    IconChevronsRight,
    IconChevronDown,
} from "@tabler/icons-react";
import { useStyles } from "./useTableStyles";
import SearchInput from "./SearchInput";
import ScrollControl from "../common/ScrollControl";
import { useTableSort } from "../../hooks";
import { useDebouncedValue } from "@mantine/hooks";

const isHeaderGroup = (row: Row | HeaderGroup): row is HeaderGroup => {
    return 'headers' in row;
}

const Table = ({
    columns: tableColumns,
    data: tableData,
    totalPages,
    onInteraction,
    searchable = false,
    showOrdering = true,
    headerItems,
    pageIndex: pageNumber,
    pageSize: size = 20,
    onRowSelect
}: TableProps) => {

    const { classes } = useStyles();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const isMounted = React.useRef(false);
    const [ searchValue, setSearchValue ] = React.useState('');
    const { sort, getSort, setSort } = useTableSort();
    const [ debouncedSearchValue ] = useDebouncedValue(searchValue, 500);

    const data = React.useMemo(() => {
        return tableData;
    }, [tableData]);

    const columns: Column[] = React.useMemo(() => {
        return tableColumns
    }, [tableColumns]);

    const tableInstance = useTable({
        columns,
        data,
        initialState: {
            pageIndex: 0,
            pageSize: size
        },
        pageCount: totalPages,
        manualPagination: true,
        defaultColumn: {
            defaultCanSort: true
        },
        getRowId(row) {
            // @ts-expect-error
            return row._id;
        }
    },
        usePagination,
        useRowSelect,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {
            pageIndex,
            pageSize,
            selectedRowIds
        }
    } = tableInstance;

    React.useEffect(() => {
        if (onInteraction) {
            if (isMounted.current) {
                onInteraction({
                    pageNumber: pageIndex + 1,
                    pageSize,
                    query: debouncedSearchValue ?? undefined,
                    sort
                })
            } else {
                isMounted.current = true;
            }
        }
    }, [pageIndex, pageSize, debouncedSearchValue]);

    React.useEffect(() => {
        if (pageNumber !== undefined && pageNumber !== pageIndex) {
            gotoPage(pageNumber);
        }
    }, [pageNumber]);

    React.useEffect(() => {
        if (size) {
            setPageSize(size);
        }
    }, [size]);

    React.useEffect(() => {
        const ids = Object.keys(selectedRowIds);
        if (onRowSelect) {
            onRowSelect(ids ?? []);
        }
    }, [selectedRowIds])

    const handleSort = (id: string) => {
        const sortObj = getSort(id);
        setSort(sortObj);
        onInteraction?.({
            pageNumber: 1,
            pageSize,
            query: searchValue ?? undefined,
            sort: sortObj
        })
    }

    const renderCheckbox = (row: Row | HeaderGroup) => {

        if (!onRowSelect) {
            return null;
        }

        const isHeader = isHeaderGroup(row);
        const props: CheckboxProps = {
            size: 'xs',
            indeterminate: isHeader && tableInstance.isAllRowsSelected,
        }

        if (isHeader) {
            props.checked = tableInstance.isAllRowsSelected;
            props.onChange = () => tableInstance.toggleAllRowsSelected(!tableInstance.isAllRowsSelected);
        } else {
            props.checked = row.getToggleRowSelectedProps().checked;
            props.onChange = row.getToggleRowSelectedProps().onChange;
        }
        return (
            <Checkbox
                indeterminate={isHeader}
                {...props}
            />
        )
    }

    return (
        <div className={classes.wrapper}>
            {
                searchable && (
                    <div className={classes.header}>
                        <SearchInput onChange={setSearchValue}/>
                        {headerItems}
                    </div>
                )
            }
            <div className={classes.tableWrapper} ref={containerRef}>
                <table {...getTableProps({ className: styles.table })}>
                <thead>
                {// Loop over the header rows
                    headerGroups.map(headerGroup => (
                        // Apply the header row props
                        <tr {...headerGroup.getHeaderGroupProps({ className: classes.headerRow })}>
                            <th className={styles.headerCell}>{renderCheckbox(headerGroup)}</th>
                            <th className={styles.headerCell}>№</th>
                            {// Loop over the headers in each row 🔽 🔼
                                headerGroup.headers.map(column => {
                                    let sortText = '';
                                    if (sort?.[column.id] === 1) {
                                        sortText = '🔼'
                                    } else if (sort?.[column.id] === -1) {
                                        sortText = '🔽'
                                    }
                                    return (
                                        // Apply the header cell props
                                        <th
                                            {...column.getHeaderProps({ className: styles.headerCell })}
                                            onClick={() => column.defaultCanSort && handleSort(column.id)}
                                        >
                                            {// Render the header
                                                column.render('Header')
                                            }
                                            {sortText}
                                        </th>
                                    )
                                })}
                        </tr>
                    ))}
                </thead>
                {/* Apply the table body props */}
                <tbody {...getTableBodyProps({ className: styles.body })}>
                {// Loop over the table rows
                    page.map(row => {
                        // Prepare the row for display
                        prepareRow(row)
                        const orderNumber = pageSize * pageIndex + row.index + 1;
                        return (
                            // Apply the row props
                            <tr {...row.getRowProps({ className: styles.row })}>
                                <td className={styles.cell}>{renderCheckbox(row)}</td>
                                <td className={styles.cell}>{orderNumber}</td>
                                {// Loop over the rows cells
                                    row.cells.map(cell => {
                                        // Apply the cell props
                                        return (
                                            <td {...cell.getCellProps({
                                                className:  styles.cell
                                            })}>
                                                {// Render the cell contents
                                                    cell.render('Cell')}
                                            </td>
                                        )
                                    })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            </div>
            <div className={classes.paginationWrapper}>
                <Flex gap='xs'>
                    <Select
                        className={classes.pageSize}
                        rightSection={<IconChevronDown size={14} />}
                        rightSectionWidth={30}
                        styles={{ rightSection: { pointerEvents: 'none' } }}
                        data={['10', '20', '30', '40', '50']}
                        size={'sm'}
                        value={pageSize.toString()}
                        onChange={(value) => {
                            if (value) {
                                setPageSize(Number(value))
                            }
                        }}
                    />
                    <ScrollControl
                        vertical={containerRef}
                        horizontal={containerRef}
                    />
                </Flex>
                <div className={classes.pagination}>
                    <span>
                        Хуудас{' '}
                        <strong>
                            {pageIndex + 1} / {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <ActionIcon onClick={() => gotoPage(0)} disabled={!canPreviousPage} variant={'filled'} color={'color-primary.6'}>
                        <IconChevronsLeft size={15}/>
                    </ActionIcon>
                    <ActionIcon onClick={() => previousPage()} disabled={!canPreviousPage} variant={'filled'} color={'color-primary.6'}>
                        <IconChevronLeft size={15}/>
                    </ActionIcon>
                    <ActionIcon onClick={() => nextPage()} disabled={!canNextPage} variant={'filled'} color={'color-primary.6'}>
                        <IconChevronRight size={15}/>
                    </ActionIcon>
                    <ActionIcon onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} variant={'filled'} color={'color-primary.6'}>
                        <IconChevronsRight size={15}/>
                    </ActionIcon>
                </div>
            </div>
        </div>
    )
};

export default Table;